/**
 * Wave 7 — Mobilization cockpit: readiness cards, Approval, project activation.
 */
import React from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  List,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_EPC_PROJECT_MOBILIZATION } from '@/constants/approval-entity-types';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { formatDateTime } from '@/utils/format';
import {
  buildMobilizationReadiness,
  readinessTagColor,
  type MobilizationReadinessState,
} from './mobilizationReadiness';

const { Text, Title } = Typography;

export type EpcMobilizationCockpitProps = {
  mobilizationId: string;
  onUpdated?: () => void;
};

export const EpcMobilizationCockpit: React.FC<EpcMobilizationCockpitProps> = ({
  mobilizationId,
  onUpdated,
}) => {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const detailQ = useQuery({
    queryKey: ['epc-mobilization', mobilizationId],
    queryFn: () => epcCommercialService.getMobilization(mobilizationId),
    enabled: Boolean(mobilizationId),
    staleTime: 10_000,
  });

  const mob = detailQ.data || null;
  const items = (mob?.items as Record<string, unknown>[]) || [];
  const readiness = mob
    ? buildMobilizationReadiness({ mobilization: mob, items })
    : null;
  const status = String(mob?.status || 'Draft');
  const contractId = String(mob?.contract_id || mob?.contractId || '');
  const projectId = String(mob?.project_id || mob?.projectId || '');
  const editable = ['Draft', 'Rejected'].includes(status);

  const patchItemM = useMutation({
    mutationFn: ({ itemId, isComplete }: { itemId: string; isComplete: boolean }) =>
      epcCommercialService.patchMobilizationItem(mobilizationId, itemId, {
        isComplete,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['epc-mobilization', mobilizationId] });
      qc.invalidateQueries({ queryKey: ['epc-mobilizations'] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Update failed'),
  });

  const submitM = useMutation({
    mutationFn: () => epcCommercialService.submitMobilization(mobilizationId),
    onSuccess: () => {
      message.success('Mobilization submitted for approval');
      qc.invalidateQueries({ queryKey: ['epc-mobilization', mobilizationId] });
      qc.invalidateQueries({ queryKey: ['epc-mobilizations'] });
      qc.invalidateQueries({
        queryKey: ['approval-summary', ENTITY_TYPE_EPC_PROJECT_MOBILIZATION, mobilizationId],
      });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Submit failed'),
  });

  const activateM = useMutation({
    mutationFn: () => epcCommercialService.activateProjectFromMobilization(mobilizationId),
    onSuccess: (row) => {
      message.success('Project activated — open Cost Budget to build Original / Current Budget');
      qc.invalidateQueries({ queryKey: ['epc-mobilization', mobilizationId] });
      qc.invalidateQueries({ queryKey: ['epc-mobilizations'] });
      onUpdated?.();
      const pid = String(row.project_id || row.projectId || '');
      const awardId = String(
        row.award_id || row.awardId || (row as any).contract?.award_id || ''
      );
      if (pid) {
        const qs = awardId ? `?awardId=${encodeURIComponent(awardId)}` : '';
        navigate(`/prj/projects/${pid}/budgets${qs}`);
      }
    },
    onError: (e: Error) => message.error(e.message || 'Activation failed'),
  });

  if (detailQ.isLoading || !mob) {
    return <Alert type="info" message="Loading mobilization…" />;
  }

  if (detailQ.isError) {
    return (
      <Alert
        type="error"
        message="Unable to load mobilization"
        description={(detailQ.error as Error)?.message}
      />
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div>
        <Space wrap style={{ marginBottom: 8 }}>
          <Title level={5} style={{ margin: 0 }}>
            {String(mob.mobilization_number || 'Mobilization')}
          </Title>
          <Tag>{status}</Tag>
          {readiness ? (
            <Tag color={readinessTagColor(readiness.overall as MobilizationReadinessState)}>
              {readiness.overall}
            </Tag>
          ) : null}
        </Space>
        <Text type="secondary">{String(mob.title || '')}</Text>
      </div>

      <Card
        size="small"
        title={
          <Space wrap>
            <span>Readiness checklist</span>
            {readiness ? (
              <Tag>
                {readiness.requiredComplete}/{readiness.requiredTotal} required
              </Tag>
            ) : null}
          </Space>
        }
      >
        <List
          size="small"
          dataSource={readiness?.cards || []}
          locale={{ emptyText: 'No checklist items' }}
          renderItem={(c) => (
            <List.Item>
              <Checkbox
                checked={c.complete}
                disabled={!editable || patchItemM.isPending}
                onChange={(e) =>
                  patchItemM.mutate({ itemId: c.itemId, isComplete: e.target.checked })
                }
              >
                <Space wrap>
                  <Text>{c.title}</Text>
                  <Tag color={readinessTagColor(c.state)}>{c.state}</Tag>
                  {c.required ? <Tag>Required</Tag> : <Tag>Optional</Tag>}
                  <Text type="secondary">{c.itemCode}</Text>
                </Space>
              </Checkbox>
            </List.Item>
          )}
        />
      </Card>

      {readiness ? (
        <Card size="small" title="Blockers">
          {!readiness.blockers.length ? (
            <Alert type="success" showIcon message="No blockers" />
          ) : (
            <List
              size="small"
              dataSource={readiness.blockers}
              renderItem={(b) => (
                <List.Item>
                  <Tag color={b.severity === 'blocking' ? 'error' : 'warning'}>
                    {b.severity}
                  </Tag>
                  <Text>{b.reason}</Text>
                </List.Item>
              )}
            />
          )}
        </Card>
      ) : null}

      <Card size="small" title="Submit & activate">
        <Space wrap>
          {contractId ? (
            <Button onClick={() => navigate(`/epc/commercial/contracts/${contractId}`)}>
              Open contract (CA)
            </Button>
          ) : null}
          <Button
            type="primary"
            disabled={!readiness?.canSubmit}
            loading={submitM.isPending}
            title={readiness?.submitBlockedReason}
            onClick={() => submitM.mutate()}
          >
            Submit for approval
          </Button>
          <Button
            disabled={!readiness?.canActivate}
            loading={activateM.isPending}
            title={readiness?.activateBlockedReason}
            onClick={() => activateM.mutate()}
          >
            Activate project
          </Button>
          {projectId ? (
            <Button type="link" onClick={() => navigate(`/prj/projects/${projectId}/edit`)}>
              Open project
            </Button>
          ) : null}
        </Space>
        {readiness?.submitBlockedReason && !readiness.canSubmit ? (
          <Alert
            style={{ marginTop: 8 }}
            type="info"
            showIcon
            message={readiness.submitBlockedReason}
          />
        ) : null}
        {readiness?.activateBlockedReason && !readiness.canActivate && !projectId ? (
          <Alert
            style={{ marginTop: 8 }}
            type="info"
            showIcon
            message={readiness.activateBlockedReason}
          />
        ) : null}
        {mob.activated_at ? (
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Activated: {formatDateTime(String(mob.activated_at))}
          </Text>
        ) : null}
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Submit starts Approval Runtime (`epc_project_mobilization`). Activate runs only after
          Approved — FE never sets Activated alone.
        </Text>
      </Card>

      <Card size="small" title="Approval">
        <ApprovalPanel
          entityType={ENTITY_TYPE_EPC_PROJECT_MOBILIZATION}
          entityId={mobilizationId}
          extraInvalidateQueryKeys={[
            ['epc-mobilizations'],
            ['epc-mobilization', mobilizationId],
          ]}
        />
      </Card>
    </Space>
  );
};

export default EpcMobilizationCockpit;
